import { Dispatch, SetStateAction, useContext } from 'react';
import { IJobMeta, useBackgroundMutation } from '../../utils/BackgroundJobs';
import NominalJournalPostMutation from '../../graphql/mutations/nominal/NominalJournalPostMutation.graphql';
import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import NominalJournalPostingPreview from '../../graphql/entities/nominal/NominalJournalPostingPreview';
import NominalJournalTransactions from '../../graphql/entities/nominal/NominalJournalTransactions';
import { PostSelectButton } from '../../components/SelectButton';
import { InfoButton } from '../../components/connectedForm/Button';
import { NominalJournal } from '../../graphql/entities';
import { IFormAction } from '../../@types/entityTypes';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { INominalJournalPostMutationResult, INominalJournalPostMutationVariables } from '../../graphql/schema/p4types';
import { useDispatch } from 'react-redux';
import generateClientMutationId from '../../graphql/schema/generateClientMutationId';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useHistory } from 'react-router';

const NOMINAL_JOURNAL_POSTING_PREVIEW = createPostingPreview(NominalJournalPostingPreview);
const NOMINAL_JOURNAL_POSTED_TRANSACTIONS = createPostedTransactions(NominalJournalTransactions);

export enum NominalJournalActions {
  PreviewPostings = 'NOMINAL_JOURNAL_POSTING_PREVIEW',
  ViewTransactions = 'NOMINAL_JOURNAL_POSTED_TRANSACTIONS',
  Reverse = 'NOMINAL_JOURNAL_REVERSE',
  PostJournal = 'NOMINAL_JOURNAL_POST JOURNAL',
  Import = 'NOMINAL_JOURNAL_IMPORT',
}

export const formActionsForPermissions = [
  { actionReference: NominalJournalActions.PreviewPostings },
  { actionReference: NominalJournalActions.ViewTransactions },
  { actionReference: NominalJournalActions.Reverse },
  { actionReference: NominalJournalActions.PostJournal },
  { actionReference: NominalJournalActions.Import },
];

export const useNominalJournalFormActions = (
  setImportModalIsOpen: Dispatch<SetStateAction<boolean>>,
  setDynamicReportModalOpen: Dispatch<SetStateAction<boolean>>
) => {
  const [performSave] = usePerformSave();
  const dispatch = useDispatch();
  const history = useHistory();

  const connectedForm = useContext(ConnectedFormContext);
  const { match, submitOptions = [] } = connectedForm;

  const canBePosted = (journal) => {
    if (journal && journal.documentStatus) {
      return journal.documentStatus === 'DRAFT';
    }
    return false;
  };

  const displayPreview = (values) => {
    if (values && values.documentStatus) {
      return values.documentStatus === 'DRAFT' || values.documentStatus === 'RELEASED';
    }
    return false;
  };

  const displayPosted = (values) => {
    if (values && values.documentStatus) {
      return values.documentStatus === 'POSTED';
    }
    return false;
  };

  const displayImport = (values) => {
    return !(values && values?.journalLines && values.journalLines.length > 0);
  };

  const [postJournalMutation] = useBackgroundMutation<
    INominalJournalPostMutationResult,
    INominalJournalPostMutationVariables
  >(NominalJournalPostMutation, {
    jobTitle: 'Post Nominal Journal',
    onCompleteJob: (job: IJobMeta) => {
      if (connectedForm.reportOnCompletion) {
        setDynamicReportModalOpen(true);
      } else {
        connectedForm.afterSubmit?.();
      }
    },
  });

  const postJournal: IFormAction<NominalJournalActions.PostJournal> = {
    key: 'postJournal',
    text: 'Post Journal',
    individualButton: true,
    individualButtonComponent: PostSelectButton,
    actionReference: NominalJournalActions.PostJournal,
    onClick: () => {
      // If not new then the post & add new option won't be available
      const params = match?.params;
      if (`${params?.id || params?.uuid}`.toLowerCase() !== 'new') {
        const [selectedSubmitOption, dispatchSubmitOptions] = submitOptions;
        dispatchSubmitOptions?.({ type: 'EXIT' });
      }

      performSave({
        submitMeta: { suppressAfterSubmit: true },
        onSaveComplete: ({ result }) => {
          postJournalMutation({
            variables: {
              input: {
                clientMutationId: generateClientMutationId(),
                id: result.id,
              },
            },
          });
        },
      });
    },
    condition: canBePosted,
  };

  const showImportModal = (props) => {
    setImportModalIsOpen(true);
  };
  return [
    {
      description: 'Import',
      component: InfoButton,
      fn: showImportModal,
      individualButton: true,
      performSave: false,
      condition: displayImport,
    },
    {
      description: 'Preview Postings',
      triggerModal: {
        modalContent: NOMINAL_JOURNAL_POSTING_PREVIEW,
        entityId: 'id',
        pageView: true,
        modalProps: {
          formEntityCustomUrl: 'nominal/Postings/nominal/NominalJournal',
          forceReadOnly: true,
        },
      },
      performSave: true,
      saveMessage: 'Nominal/VAT Postings Preview',
      btnClass: 'btnInfo',
      individualButton: true,
      condition: displayPreview,
    },
    {
      description: 'View Transactions',
      triggerModal: {
        modalContent: NOMINAL_JOURNAL_POSTED_TRANSACTIONS,
        entityId: 'id',
        pageView: true,
        modalProps: {
          formEntityCustomUrl: 'nominal/Transactions/nominal/NominalJournal',
          forceReadOnly: true,
        },
      },
      btnClass: 'btnInfo',
      individualButton: true,
      condition: displayPosted,
    },
    postJournal,
    {
      description: 'Reverse',
      component: InfoButton,
      fn: (props) => {
        history.replace({
          pathname: `${NominalJournal.formUrl}New`,
          state: {
            modal: true,
            popover: false,
            duplicateId: props.values.id,
            previousLocation: NominalJournal.workspaceUrl || 'back',
            additionalDuplicateVariables: {
              reversing: true,
            },
          },
        });
      },
      individualButton: true,
      condition: displayPosted,
    },
  ];
};
